import * as React from 'react';
import {
    CreativeClickProps,
    logCreativeClick,
    logCreativeLoad,
} from '../../utils/logger';
import { TImprovedContentMarketing } from '../../../shared/types/contentMarketing';
import { useEffect, useMemo, useState } from 'react';
import { getConfig } from '../../config';
import SponsorHeader from './components/sponsor-header';

type TUsePulseLoadAndClick = {
    positionId: string;
    contentMarketing: TImprovedContentMarketing;
    isMultipleAds: boolean;
    multiAdIndex: number;
};

const usePulseLoadAndClick = ({
    positionId,
    contentMarketing,
    isMultipleAds,
    multiAdIndex,
}: TUsePulseLoadAndClick): CreativeClickProps => {
    const [pulseInitiated, setPulseInitiated] = useState(false);
    const { vertical, subvertical } = useMemo(() => {
        const config = getConfig();
        return {
            vertical: config.vertical,
            subvertical: config.subvertical,
            deviceType: config.deviceType,
        };
    }, []);

    const { adType, elementType } = useMemo(() => {
        return {
            adType: 'Content',
            elementType: isMultipleAds
                ? multiAdIndex === 0
                    ? 'Split Left'
                    : 'Split Right'
                : undefined,
        };
    }, [isMultipleAds, multiAdIndex]);

    const creativeClickObject: CreativeClickProps = useMemo(() => {
        return {
            creativeId: contentMarketing.id,
            elementId: positionId,
            verticalName: vertical,
            subVertical: subvertical,
            targetId: contentMarketing.href,
            provider: contentMarketing.sponsorData.name,
            adType,
            elementType,
            adFormat: isMultipleAds ? 'Split-view' : 'Single-view',
        };
    }, [
        contentMarketing,
        vertical,
        subvertical,
        adType,
        elementType,
        isMultipleAds,
        positionId,
    ]);

    useEffect(() => {
        if (!pulseInitiated && document) {
            if (contentMarketing) {
                logCreativeLoad({
                    creativeId: contentMarketing.id,
                    elementId: positionId,
                    verticalName: vertical,
                    subVertical: subvertical,
                    elementType,
                    adType,
                    adFormat: isMultipleAds ? 'Split-view' : 'Single-view',
                });
                setPulseInitiated(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [positionId, pulseInitiated]);

    return creativeClickObject;
};

type TContentMarketingElement = {
    positionId: string;
    multiAdIndex?: number;
    contentMarketing: TImprovedContentMarketing;
    children: React.ReactNode;
    isMultipleAds?: boolean;
};

export const ContentMarketingElement = ({
    positionId,
    contentMarketing,
    children,
    isMultipleAds = false,
    multiAdIndex = undefined,
}: TContentMarketingElement) => {
    const creativeClickObject = usePulseLoadAndClick({
        positionId,
        contentMarketing,
        isMultipleAds,
        multiAdIndex,
    });

    return (
        <article
            onClick={() => logCreativeClick(creativeClickObject)}
            className={'h-full w-full'}
            id={positionId}
        >
            {children}
        </article>
    );
};

/**
 *
 * @param children
 * @param placementId
 * @param contentMarketingList
 * @param containerClassName
 * @param flexGap
 * @constructor
 * @return a list of elements with a render props which returns contentMarketing and index
 */
export const MultiWrapper = ({
    children,
    placementId,
    contentMarketingList,
    containerClassName,
    flexGap = 'gap-8',
}) => {
    const { deviceType, vertical } = getConfig();

    const carouselStyle =
        vertical === 'realestate'
            ? {
                  width: `calc(100vw)`,
                  marginLeft: -22,
                  paddingLeft: 16,
                  paddingRight: 22,
              }
            : {
                  width: 'calc(100vw + 16px)',
                  marginLeft: -32,
                  paddingLeft: 32,
                  paddingRight: 32,
              };
    if (deviceType === 'mobile') {
        return (
            <div className={containerClassName}>
                {Array.isArray(contentMarketingList) && (
                    <SponsorHeader contentMarketing={contentMarketingList[0]} />
                )}
                <div
                    className={'cm-carousel-container flex flex-row' + flexGap}
                    style={carouselStyle}
                >
                    {contentMarketingList.map(
                        (
                            contentMarketing: TImprovedContentMarketing,
                            i: number,
                        ) => (
                            <div
                                key={i}
                                className={
                                    'cm-carousel-child flex-auto w-2/4 m-4'
                                }
                            >
                                <div className={'h-full'}>
                                    <ContentMarketingElement
                                        key={contentMarketing.id}
                                        positionId={
                                            placementId + `_split_${i + 1}`
                                        }
                                        contentMarketing={contentMarketing}
                                        multiAdIndex={i}
                                        isMultipleAds={true}
                                    >
                                        <div
                                            data-testid={'cm-carousel-block'}
                                            className={'flex flex-col h-full'}
                                            style={{ width: '70vw' }}
                                        >
                                            {children(contentMarketing, i)}
                                        </div>
                                    </ContentMarketingElement>
                                </div>
                            </div>
                        ),
                    )}
                </div>
            </div>
        );
    }
    return (
        <div className={containerClassName}>
            {Array.isArray(contentMarketingList) && (
                <SponsorHeader contentMarketing={contentMarketingList[0]} />
            )}
            <div className={'flex flex-row ' + flexGap}>
                {contentMarketingList.map(
                    (
                        contentMarketing: TImprovedContentMarketing,
                        i: number,
                    ) => (
                        <div key={i} className={'flex-auto w-2/4'}>
                            <div className={'h-full'}>
                                <ContentMarketingElement
                                    key={contentMarketing.id}
                                    positionId={placementId + `_split_${i + 1}`}
                                    contentMarketing={contentMarketing}
                                    multiAdIndex={i}
                                    isMultipleAds={true}
                                >
                                    {children(contentMarketing, i)}
                                </ContentMarketingElement>
                            </div>
                        </div>
                    ),
                )}
            </div>
        </div>
    );
};
